import { ToolTypes } from '@labradorsports/constants';
import type { FieldPiece, FrameSnapshot, Move } from '@labradorsports/play-rendering';

export function collectFieldPlayersForSpotlight(frame: FrameSnapshot): FieldPiece[] {
    const byId = new Map<number, FieldPiece>();

    const add = (list: FieldPiece[]) => {
        list.forEach((player) => byId.set(player.id, player));
    };

    add(frame.getPlayers([ToolTypes.BLUEPLAYER], frame.goalies));
    add(frame.getPlayers([ToolTypes.ORANGEPLAYER], frame.goalies));
    add(frame.visibleGoalies());
    add(frame.coaches);

    return Array.from(byId.values());
}

export function getSpotlightPlayerLabel(
    player: FieldPiece,
    frame: FrameSnapshot,
    roleDisplayMap: PlayConfigSpec['RoleDisplay']
): string {
    const role = player.props?.frameRole ?? player.props?.role;
    const roleDisplay = role ? roleDisplayMap[role] : null;
    const positionShorthand = typeof roleDisplay === 'string' ? roleDisplay : '';
    const number = getPlayerDisplayLabel(player, frame);

    return positionShorthand ? `${positionShorthand}${number}` : number;
}

export function getSpotlightPlayerLabels(
    spotlightPlayerIds: number[],
    frame: FrameSnapshot,
    roleDisplayMap: PlayConfigSpec['RoleDisplay']
): string[] {
    const playersById = new Map(collectFieldPlayersForSpotlight(frame).map((player) => [player.id, player]));

    return spotlightPlayerIds
        .map((id) => playersById.get(id))
        .filter((player): player is FieldPiece => Boolean(player))
        .map((player) => getSpotlightPlayerLabel(player, frame, roleDisplayMap));
}

export function generatePlayConceptId(): string {
    return `pc_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function getPlayConceptInstanceCounts(playConcepts: PlayConcept[]): {
    instanceCountsByConceptId: Map<string, number>;
    instanceNumbers: number[];
} {
    const instanceCountsByConceptId = new Map<string, number>();
    const instanceNumbers: number[] = [];
    const seen = new Map<string, number>();

    playConcepts.forEach((playConcept) => {
        instanceCountsByConceptId.set(
            playConcept.conceptId,
            (instanceCountsByConceptId.get(playConcept.conceptId) ?? 0) + 1
        );

        const next = (seen.get(playConcept.conceptId) ?? 0) + 1;
        seen.set(playConcept.conceptId, next);
        instanceNumbers.push(next);
    });

    return { instanceCountsByConceptId, instanceNumbers };
}

export function resolvePlayConceptFields(
    playConcept: PlayConcept,
    playbookConcept: ConceptDefinition | undefined
): { name: string | undefined; description: string | undefined; tags: string[] } {
    return {
        name: playbookConcept?.name ?? playConcept.name,
        description: playbookConcept?.description ?? playConcept.description,
        tags: playbookConcept?.tags ?? playConcept.tags ?? [],
    };
}

export function getPlayConceptDisplayName(
    conceptName: string | undefined,
    instanceNumber: number,
    totalInstances: number
): string {
    if (!conceptName) {
        return 'Concept';
    }

    if (totalInstances <= 1) {
        return conceptName;
    }

    return `${conceptName} (${instanceNumber})`;
}

export function getActivePlayConceptDisplayName(
    playConcepts: PlayConcept[] | undefined,
    activePlayConceptId: string | null | undefined,
    allConcepts: ConceptDefinition[]
): string | null {
    if (!activePlayConceptId || !playConcepts?.length) {
        return null;
    }

    const conceptById = new Map(allConcepts.map((concept) => [concept.id, concept]));
    const activeIndex = playConcepts.findIndex((concept) => concept.id === activePlayConceptId);

    if (activeIndex === -1) {
        return null;
    }

    const playConcept = playConcepts[activeIndex];
    const concept = conceptById.get(playConcept.conceptId);
    const { name } = resolvePlayConceptFields(playConcept, concept);
    const { instanceCountsByConceptId, instanceNumbers } = getPlayConceptInstanceCounts(playConcepts);
    const totalInstances = instanceCountsByConceptId.get(playConcept.conceptId) ?? 1;

    return getPlayConceptDisplayName(name, instanceNumbers[activeIndex], totalInstances);
}

export function formatConceptTagSummary(playerLabels: string[]): string {
    if (playerLabels.length === 0) {
        return '';
    }

    return `Tag ${playerLabels.join(', ')} in a reusable concept.`;
}

export function getPlayerDisplayLabel(player: FieldPiece | null | undefined, frame: FrameSnapshot | null | undefined) {
    if (!player || !frame) {
        return '?';
    }

    if (player.props?.jersey) {
        return String(player.props.jersey);
    }

    const piece = frame.field.getFieldPiece(player.props?.pieceId);

    if (piece?.type === ToolTypes.GOAL) {
        return '00';
    }

    const teamPlayers = frame.getPlayers([player.type], frame.goalies);
    const idx = teamPlayers.findIndex((p) => p.id === player.id);

    if (idx >= 0) {
        return String(idx + 1);
    }

    return '?';
}

export function getMoveEditorPlayer(move: Move, frame: FrameSnapshot | null | undefined): FieldPiece | null {
    if (!frame) {
        return null;
    }

    if (typeof move.props?.ball === 'number') {
        return frame.getCarrier(move.props.ball);
    }

    if (typeof move.source === 'number') {
        return frame.getPlayer(move.source);
    }

    return frame.getPlayer(move.target);
}
